import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoImage from "./images/logo_utility_web.png";

function OpeningPage() {
  const navigate = useNavigate();
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    document.title = "Utility Web";

    const closeTimer = setTimeout(() => {
      setClosing(true);
    }, 4300);

    const navigateTimer = setTimeout(() => {
      navigate("/home");
    }, 5000);

    return () => {
      clearTimeout(closeTimer);
      clearTimeout(navigateTimer);
    };
  }, [navigate]);

  return (
    <>
      <style>{`
        * {
          box-sizing: border-box;
        }

        html,
        body {
          margin: 0;
          background: #050505;
        }

        body {
          overflow: hidden;
        }

        .uw-opening {
          --gold: #ebb51b;

          position: fixed;
          inset: 0;
          z-index: 999999;

          width: 100vw;
          height: 100dvh;

          display: flex;
          align-items: center;
          justify-content: center;

          overflow: hidden;

          background:
            radial-gradient(
              circle at 50% 50%,
              #111 0%,
              #080808 40%,
              #030303 100%
            );

          font-family:
            Inter,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
        }

        /* ========================================
           TEXTURE
        ======================================== */

        .uw-opening::before {
          content: "";

          position: absolute;
          inset: 0;

          opacity: 0.025;

          background-image:
            repeating-linear-gradient(
              0deg,
              #fff 0,
              #fff 1px,
              transparent 1px,
              transparent 4px
            );

          pointer-events: none;
        }

        /* ========================================
           LINEA ORIZZONTALE
        ======================================== */

        .uw-opening-line {
          position: absolute;

          top: 50%;
          left: 50%;

          width: 0;
          height: 1px;

          transform: translate(-50%, -50%);

          background: linear-gradient(
            90deg,
            transparent,
            rgba(235, 181, 27, 0.2),
            #ebb51b,
            rgba(235, 181, 27, 0.2),
            transparent
          );

          opacity: 0;

          animation: uwLine 1.2s 0.2s ease forwards;
        }

        @keyframes uwLine {
          0% {
            width: 0;
            opacity: 0;
          }

          20% {
            opacity: 1;
          }

          100% {
            width: min(850px, 85vw);
            opacity: 0.25;
          }
        }

        /* ========================================
           CONTENUTO
        ======================================== */

        .uw-opening-content {
          position: relative;
          z-index: 10;

          display: flex;
          flex-direction: column;
          align-items: center;

          width: min(600px, 90vw);

          text-align: center;
        }

        /* ========================================
           LOGO
        ======================================== */

        .uw-logo-container {
          position: relative;

          width: 260px;
          height: 220px;

          display: flex;
          align-items: center;
          justify-content: center;
        }

        .uw-logo {
          width: 230px;
          max-width: 65vw;
          height: auto;

          opacity: 0;

          transform:
            translateY(15px)
            scale(0.92);

          filter: blur(7px);

          animation:
            uwLogoReveal
            1.4s
            0.55s
            cubic-bezier(0.16, 1, 0.3, 1)
            forwards;
        }

        @keyframes uwLogoReveal {
          0% {
            opacity: 0;

            transform:
              translateY(15px)
              scale(0.92);

            filter: blur(7px);
          }

          100% {
            opacity: 1;

            transform:
              translateY(0)
              scale(1);

            filter: blur(0);
          }
        }

        /* ========================================
           PICCOLO TRATTO DORATO
        ======================================== */

        .uw-logo-mark {
          position: absolute;

          bottom: 4px;
          left: 50%;

          width: 0;
          height: 2px;

          transform: translateX(-50%);

          background: var(--gold);

          animation:
            uwMark
            0.8s
            1.3s
            cubic-bezier(0.16, 1, 0.3, 1)
            forwards;
        }

        @keyframes uwMark {
          to {
            width: 45px;
          }
        }

        /* ========================================
           TITOLO
        ======================================== */

        .uw-opening-title {
          margin: 27px 0 0;

          color: #fff;

          font-size: clamp(27px, 4vw, 40px);
          font-weight: 300;

          letter-spacing: 13px;

          text-transform: uppercase;

          opacity: 0;

          transform: translateY(12px);

          animation:
            uwTitle
            1s
            1.45s
            ease
            forwards;
        }

        .uw-opening-title strong {
          color: var(--gold);

          font-weight: 700;
        }

        @keyframes uwTitle {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* ========================================
           SUBTITLE
        ======================================== */

        .uw-opening-subtitle {
          margin: 13px 0 0;

          color: rgba(255, 255, 255, 0.3);

          font-size: 9px;
          font-weight: 500;

          letter-spacing: 4px;

          text-transform: uppercase;

          opacity: 0;

          animation:
            uwSubtitle
            0.8s
            2s
            ease
            forwards;
        }

        @keyframes uwSubtitle {
          to {
            opacity: 1;
          }
        }

        /* ========================================
           LOADING
        ======================================== */

        .uw-loader {
          position: relative;

          width: min(270px, 65vw);
          height: 1px;

          margin-top: 50px;

          background: rgba(255, 255, 255, 0.08);

          overflow: hidden;

          opacity: 0;

          animation:
            uwLoaderAppear
            0.4s
            2.25s
            forwards;
        }

        @keyframes uwLoaderAppear {
          to {
            opacity: 1;
          }
        }

        .uw-loader-fill {
          width: 0;
          height: 100%;

          background: var(--gold);

          animation:
            uwLoading
            1.8s
            2.3s
            cubic-bezier(0.65, 0, 0.35, 1)
            forwards;
        }

        @keyframes uwLoading {
          0% {
            width: 0%;
          }

          25% {
            width: 18%;
          }

          45% {
            width: 43%;
          }

          65% {
            width: 68%;
          }

          85% {
            width: 88%;
          }

          100% {
            width: 100%;
          }
        }

        /* ========================================
           READY
        ======================================== */

        .uw-ready {
          margin-top: 13px;

          color: rgba(255, 255, 255, 0.25);

          font-size: 8px;
          font-weight: 600;

          letter-spacing: 4px;

          opacity: 0;

          animation:
            uwReady
            0.4s
            3.85s
            forwards;
        }

        @keyframes uwReady {
          to {
            opacity: 1;
          }
        }

        /* ========================================
           MICRO PARTICELLE
        ======================================== */

        .uw-dot {
          position: absolute;

          width: 2px;
          height: 2px;

          border-radius: 50%;

          background: var(--gold);

          opacity: 0;

          animation:
            uwDotFloat
            var(--duration)
            var(--delay)
            ease-in-out
            infinite;
        }

        @keyframes uwDotFloat {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }

          40% {
            opacity: 0.35;
          }

          100% {
            opacity: 0;
            transform: translateY(-50px);
          }
        }

        /* ========================================
           CHIUSURA CINEMATOGRAFICA
        ======================================== */

        .uw-curtain {
          position: absolute;

          left: 0;

          width: 100%;
          height: 50%;

          z-index: 100;

          background: #000;

          transition:
            transform
            0.7s
            cubic-bezier(0.76, 0, 0.24, 1);
        }

        .uw-curtain-top {
          top: 0;

          transform: translateY(-100%);
        }

        .uw-curtain-bottom {
          bottom: 0;

          transform: translateY(100%);
        }

        .uw-opening.closing .uw-curtain-top {
          transform: translateY(0);
        }

        .uw-opening.closing .uw-curtain-bottom {
          transform: translateY(0);
        }

        /* linea finale tra le due tende */

        .uw-final-line {
          position: absolute;

          top: 50%;
          left: 50%;

          z-index: 110;

          width: 0;
          height: 1px;

          transform: translate(-50%, -50%);

          background: var(--gold);

          opacity: 0;
        }

        .uw-opening.closing .uw-final-line {
          animation:
            uwFinalLine
            0.55s
            0.3s
            ease
            forwards;
        }

        @keyframes uwFinalLine {
          0% {
            width: 0;
            opacity: 0;
          }

          50% {
            width: 180px;
            opacity: 0.8;
          }

          100% {
            width: 0;
            opacity: 0;
          }
        }

        /* ========================================
           MOBILE
        ======================================== */

        @media (max-width: 600px) {
          .uw-logo-container {
            width: 220px;
            height: 190px;
          }

          .uw-logo {
            width: 195px;
          }

          .uw-opening-title {
            font-size: 24px;
            letter-spacing: 8px;
          }

          .uw-opening-subtitle {
            font-size: 7px;
            letter-spacing: 3px;
          }

          .uw-loader {
            margin-top: 40px;
          }
        }

        /* ========================================
           ACCESSIBILITÀ
        ======================================== */

        @media (prefers-reduced-motion: reduce) {
          .uw-opening *,
          .uw-opening *::before,
          .uw-opening *::after {
            animation-duration: 0.01ms !important;
          }
        }
      `}</style>

      <div className={`uw-opening ${closing ? "closing" : ""}`}>
        {/* linea centrale */}
        <div className="uw-opening-line" />

        {/* particelle molto discrete */}
        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={i}
            className="uw-dot"
            style={{
              left: `${8 + ((i * 17) % 84)}%`,
              top: `${15 + ((i * 29) % 70)}%`,
              "--delay": `${(i % 6) * 0.35}s`,
              "--duration": `${2.5 + (i % 4) * 0.5}s`,
            }}
          />
        ))}

        <main className="uw-opening-content">
          <div className="uw-logo-container">
            <img
              src={LogoImage}
              alt="Utility Web"
              className="uw-logo"
            />

            <div className="uw-logo-mark" />
          </div>

          <h1 className="uw-opening-title">
            UTILITY <strong>WEB</strong>
          </h1>

          <p className="uw-opening-subtitle">
            Technoparts Digital Workspace
          </p>

          <div className="uw-loader">
            <div className="uw-loader-fill" />
          </div>

          <div className="uw-ready">
            SYSTEM READY
          </div>
        </main>

        {/* chiusura finale */}
        <div className="uw-curtain uw-curtain-top" />
        <div className="uw-curtain uw-curtain-bottom" />

        <div className="uw-final-line" />
      </div>
    </>
  );
}

export default OpeningPage;


//ejmm qlwq qnse uqjt
